import { HugeiconsIcon } from "@hugeicons/react"
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon"
import Message01Icon from "@hugeicons/core-free-icons/Message01Icon"
import Link04Icon from "@hugeicons/core-free-icons/Link04Icon"
import Mail01Icon from "@hugeicons/core-free-icons/Mail01Icon"
import SmartPhone01Icon from "@hugeicons/core-free-icons/SmartPhone01Icon"
import Video01Icon from "@hugeicons/core-free-icons/Video01Icon"
import Image01Icon from "@hugeicons/core-free-icons/Image01Icon"
import GiftIcon from "@hugeicons/core-free-icons/GiftIcon"
import Tag01Icon from "@hugeicons/core-free-icons/Tag01Icon"
import ShoppingBag01Icon from "@hugeicons/core-free-icons/ShoppingBag01Icon"
import BookOpen01Icon from "@hugeicons/core-free-icons/BookOpen01Icon"
import Key01Icon from "@hugeicons/core-free-icons/Key01Icon"
import UserGroupIcon from "@hugeicons/core-free-icons/UserGroupIcon"
import UserAdd01Icon from "@hugeicons/core-free-icons/UserAdd01Icon"
import FlashIcon from "@hugeicons/core-free-icons/FlashIcon"
import ChartIncreaseIcon from "@hugeicons/core-free-icons/ChartIncreaseIcon"
import Target02Icon from "@hugeicons/core-free-icons/Target02Icon"
import Shield01Icon from "@hugeicons/core-free-icons/Shield01Icon"
import Clock01Icon from "@hugeicons/core-free-icons/Clock01Icon"
import LockIcon from "@hugeicons/core-free-icons/LockIcon"
import RepeatIcon from "@hugeicons/core-free-icons/RepeatIcon"
import QuoteUpIcon from "@hugeicons/core-free-icons/QuoteUpIcon"
import { useTranslation } from "@/lib/i18n"
import Heading from "@/components/layout/Heading"
import { Button } from "@/components/ui/button"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import type { UseCaseData } from "@/data/use-cases/use-cases-registry"
import { getRelatedUseCasesForLanguage } from "@/data/use-cases/use-cases-i18n"
import { Link } from "@tanstack/react-router"
import Iphone15Pro from "@/components/ui/iphone15"
import CTA from "@/components/LandingPage/CTA"
import { AskAIExplain } from "@/components/ask-ai-to-explain"
import { lazy, Suspense } from "react"

const Process = lazy(() => import("@/components/layout/Process"))

// ─── Icon Map ───────────────────────────────────────────────
const iconMap = {
  MessageSquare: Message01Icon, Link: Link04Icon, Mail: Mail01Icon, Smartphone: SmartPhone01Icon,
  Video: Video01Icon, Image: Image01Icon, Gift: GiftIcon, Tag: Tag01Icon,
  ShoppingBag: ShoppingBag01Icon, BookOpen: BookOpen01Icon, MessageCircle: Message01Icon,
  Key: Key01Icon, Users: UserGroupIcon, UserPlus: UserAdd01Icon, Flame: FlashIcon,
  BarChart3: ChartIncreaseIcon, Zap: FlashIcon, Target: Target02Icon,
  TrendingUp: ChartIncreaseIcon, Shield: Shield01Icon, Clock: Clock01Icon,
  Lock: LockIcon, Repeat: RepeatIcon,
} as const

function UseCaseIcon({ name, className, size = 20 }: { name: string; className?: string; size?: number }) {
  const Icon = iconMap[name as keyof typeof iconMap]
  if (!Icon) return null
  return <HugeiconsIcon icon={Icon} size={size} color="currentColor" className={className} />
}

/*
 * ─── Pixel-perfect Instagram DM Mockup ─────────────────────
 *
 * Color palette:
 *   Screen bg:      #000000
 *   Bubble gray:    #2A2A2A
 *   Separator:      #1C1C1E
 *   Input border:   #3A3A3C
 *   Muted text:     #8E8E93
 *   Purple bubble:  linear-gradient(135deg, #6C3AED → #9333EA)
 *   CTA button bg:  #3A3A3C
 *   Avatar bg:      #3A3A3C
 *   White text:     #FFFFFF
 *
 * Content designed at 389.5×843.5 base — ScaledContent handles scaling.
 */
function InstagramDMPreview({ data }: { data: UseCaseData["phonePreview"] }) {
  // Small inline avatar shown next to first bot message in a group
  const MiniAvatar = () => (
    <div className="w-[28px] h-[28px] rounded-full shrink-0 flex items-center justify-center" style={{ background: "#3A3A3C" }}>
      <span className="text-[10px] font-bold text-[#A1A1AA]">{data.profilePic}</span>
    </div>
  )

  return (
    <Iphone15Pro className="h-[28rem] sm:h-[32rem] lg:h-[36rem] w-auto shrink-0">
      <div className="w-full h-full bg-black flex flex-col text-white" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

        {/* ─── Status bar ─── */}
        <div className="flex items-center justify-between px-8 pt-4 pb-1">
          <span className="text-[15px] font-semibold tracking-tight">9:41</span>
          <div className="flex items-center gap-[5px]">
            {/* Signal */}
            <svg width="18" height="12" viewBox="0 0 18 12" fill="white"><rect x="0" y="7" width="3.5" height="5" rx="0.7" /><rect x="4.5" y="4.5" width="3.5" height="7.5" rx="0.7" /><rect x="9" y="2" width="3.5" height="10" rx="0.7" /><rect x="13.5" y="0" width="3.5" height="12" rx="0.7" opacity="0.35" /></svg>
            {/* WiFi */}
            <svg width="17" height="12" viewBox="0 0 17 12" fill="none"><path d="M8.5 11a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4z" fill="white" /><path d="M5 9c1-1.3 2.1-2 3.5-2s2.5.7 3.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" /><path d="M1.5 6c1.5-2 3.8-3.5 7-3.5s5.5 1.5 7 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" /></svg>
            {/* Battery */}
            <svg width="27" height="13" viewBox="0 0 27 13" fill="none"><rect x="0.5" y="0.5" width="23" height="12" rx="3.5" stroke="white" strokeOpacity="0.35" /><rect x="25" y="4" width="1.5" height="5" rx="0.75" fill="white" fillOpacity="0.4" /><rect x="2" y="2" width="14" height="9" rx="2" fill="white" /></svg>
          </div>
        </div>

        {/* ─── DM Header ─── */}
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Back */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 19l-7-7 7-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          {/* Avatar with IG gradient ring */}
          <div className="w-10 h-10 rounded-full p-[2.5px] shrink-0" style={{ background: "linear-gradient(135deg, #FCAF45, #E1306C, #833AB4)" }}>
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
              <span className="text-[13px] font-bold text-[#D4D4D8]">{data.profilePic}</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold leading-tight truncate">{data.username}</p>
            <p className="text-[12px] text-[#8E8E93] leading-tight">Active now</p>
          </div>
          {/* Phone */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          {/* Video */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M23 7l-7 5 7 5V7z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><rect x="1" y="5" width="15" height="14" rx="2" stroke="white" strokeWidth="1.5" /></svg>
        </div>

        {/* ─── Divider ─── */}
        <div className="h-px" style={{ background: "#1C1C1E" }} />

        {/* ─── Chat area ─── */}
        <div className="flex-1 px-3 pt-5 pb-2 flex flex-col gap-[10px] overflow-hidden">
          {data.messages.map((msg, i) => {
            const isBot = msg.from === "bot"
            // Show mini avatar only on first bot message in a consecutive group
            const showAvatar = isBot && (i === 0 || data.messages[i - 1]?.from !== "bot")

            return (
              <div key={i}>
                {isBot ? (
                  /* ── Bot / incoming messages ── */
                  <div className="flex items-end gap-[8px]">
                    {/* Mini avatar or spacer */}
                    {showAvatar ? <MiniAvatar /> : <div className="w-[28px] shrink-0" />}
                    <div className="flex flex-col gap-[6px] max-w-[75%]">
                      {/* Text bubble */}
                      <div className="rounded-[18px] px-[14px] py-[10px]" style={{ background: "#2A2A2A" }}>
                        <p className="text-[15px] leading-[20px]">{msg.text}</p>
                      </div>
                      {/* Link/CTA card — embedded inside the conversation */}
                      {msg.hasLink && (
                        <div className="rounded-[14px] overflow-hidden" style={{ background: "#2A2A2A" }}>
                          <div className="px-[14px] pt-[10px] pb-[8px]">
                            <p className="text-[13px] text-[#8E8E93] leading-tight">{msg.hasLink.url}</p>
                          </div>
                          <div className="mx-[6px] mb-[6px]">
                            <div className="rounded-[10px] py-[10px] text-center" style={{ background: "#3A3A3C" }}>
                              <span className="text-[15px] font-semibold text-white">{msg.hasLink.label}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* ── User / outgoing messages (purple) ── */
                  <div className="flex justify-end">
                    <div
                      className="rounded-[18px] px-[14px] py-[10px] max-w-[75%]"
                      style={{ background: "linear-gradient(135deg, #6D28D9, #7C3AED, #9333EA)" }}
                    >
                      <p className="text-[15px] leading-[20px]">{msg.text}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ─── Input bar ─── */}
        <div className="flex items-center gap-[10px] px-4 py-3" style={{ borderTop: "1px solid #1C1C1E" }}>
          {/* Camera */}
          <div className="w-[40px] h-[40px] rounded-full flex items-center justify-center shrink-0" style={{ border: "2px solid #3A3A3C" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="#8E8E93" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="13" r="4" stroke="#8E8E93" strokeWidth="1.5" /></svg>
          </div>
          {/* Input */}
          <div className="flex-1 rounded-full px-4 py-[9px]" style={{ border: "1px solid #3A3A3C" }}>
            <span className="text-[15px]" style={{ color: "#555" }}>Message...</span>
          </div>
          {/* Mic */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="shrink-0"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" stroke="#8E8E93" strokeWidth="1.5" /><path d="M19 10v2a7 7 0 01-14 0v-2" stroke="#8E8E93" strokeWidth="1.5" strokeLinecap="round" /><line x1="12" y1="19" x2="12" y2="23" stroke="#8E8E93" strokeWidth="1.5" strokeLinecap="round" /></svg>
          {/* Gallery */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="shrink-0"><rect x="3" y="3" width="18" height="18" rx="2" stroke="#8E8E93" strokeWidth="1.5" /><circle cx="8.5" cy="8.5" r="1.5" fill="#8E8E93" /><path d="M21 15l-5-5L5 21" stroke="#8E8E93" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>

        {/* ─── Home indicator ─── */}
        <div className="flex justify-center pb-2 pt-1">
          <div className="w-[134px] h-[5px] rounded-full" style={{ background: "#3A3A3C" }} />
        </div>
      </div>
    </Iphone15Pro>
  )
}

// ─── Main Layout Component ──────────────────────────────────
export default function UseCasePageLayout({ data }: { data: UseCaseData }) {
  const { translations: t, language } = useTranslation()
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const relatedUseCases = getRelatedUseCasesForLanguage(language, data.slug)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* ═══ Section 1: Hero — matches main site hero ═══ */}
      <section className="relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
        {/* Gradient Blobs Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-32 w-80 h-80 bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/3 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-r from-yellow-400/12 to-orange-400/12 rounded-full blur-2xl animate-pulse delay-2000" />
        </div>

        {/* Dotted Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,#80808030_1px,transparent_1px)] bg-[size:16px_16px]" />

        {/* Center gradient for readability */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,white_0%,white_20%,transparent_60%)] dark:bg-[radial-gradient(ellipse_at_center,black_0%,black_20%,transparent_60%)]" />

        <div className="relative w-full px-6 sm:px-10 lg:px-20 xl:px-28 pt-24 sm:pt-32 pb-10 sm:pb-14">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-12 lg:gap-20">
            {/* Left - Text Content */}
            <div className="flex-1 min-w-0 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center justify-center px-4 py-1 sm:px-5 sm:py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-4 sm:mb-6">
                <UseCaseIcon name={data.icon} size={14} className={`mr-1.5 ${data.color.text}`} />
                <span className="text-gray-600 dark:text-gray-300 font-semibold text-xs sm:text-sm">{data.badge}</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-neutral-900 dark:text-white leading-[1.1] tracking-tight mb-4 sm:mb-6">
                {data.hero.h1}
              </h1>

              {/* Subheadline */}
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium mb-8 sm:mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                {data.hero.subtitle}
              </p>

              {/* CTA — same as main hero */}
              <div className="mb-4 flex flex-col items-center lg:items-start">
                <Button
                  size="lg"
                  className="sm:w-auto w-full capitalize font-bold px-8 py-5 sm:px-10 sm:py-6 rounded-full bg-primary-gradient text-primary-foreground text-base shadow-md shadow-primary/20 hover:brightness-[0.97] hover:-translate-y-0.5 transition-all duration-300 group"
                  onClick={() => handleCTA(`CTA_UseCase_Hero_${data.slug}`, "Start For Free")}
                >
                  {t.useCaseLayout.startForFree}
                  <HugeiconsIcon icon={ArrowRight02Icon} size={20} color="currentColor" className="ml-2" />
                </Button>
              </div>

              {/* Helper text */}
              <p className="text-sm text-neutral-500 dark:text-neutral-400 font-medium mb-10 sm:mb-12 text-center lg:text-left">
                {t.useCaseLayout.freeToStart}
              </p>
            </div>

            {/* Right — Realistic Instagram DM Preview */}
            <div className="lg:w-[40%] flex-shrink-0 min-w-0 w-full flex justify-center lg:justify-center lg:pl-[10%]">
              <InstagramDMPreview data={data.phonePreview} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Section 2: Process (comment-to-DM only) or Overview highlights ═══ */}
      {data.slug === "instagram-comment-dm-automation" ? (
        <Suspense fallback={null}>
          <Process />
        </Suspense>
      ) : (
        <section
          className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden"
          aria-label={data.overview.body}
        >
          <div className="max-w-6xl mx-auto relative z-10">
            <Heading title={data.overview.headline} showLine={false} />
            <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-14">
              <div className="lg:w-1/2 w-full">
                <div className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden h-full">
                  <img
                    src={`/assets/features/${data.overview.assetSlug}-light.${data.overview.hasGif ? "gif" : "png"}`}
                    alt={data.overview.headline}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain dark:hidden"
                  />
                  <img
                    src={`/assets/features/${data.overview.assetSlug}-dark.${data.overview.hasGif ? "gif" : "png"}`}
                    alt={data.overview.headline}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain hidden dark:block"
                  />
                </div>
              </div>
              <div className="lg:w-1/2 flex flex-col gap-4 justify-center">
                {data.overview.highlights.map((h) => (
                  <div
                    key={h.title}
                    className="flex gap-4 items-start rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 hover:border-brand-accent/40 dark:hover:border-brand-accent/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-neutral-200/50 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-accent/10 dark:bg-brand-accent/15 flex items-center justify-center">
                      <UseCaseIcon name={h.icon} size={20} className="text-brand-accent" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-neutral-900 dark:text-white mb-1">{h.title}</h3>
                      <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{h.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ Section 3: Proof Points ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <Heading title={data.proofPoints.headline} showLine={false} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {data.proofPoints.items.map((point) => (
              <div
                key={point.label}
                className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 bg-white dark:bg-neutral-900/40 p-6 text-center"
              >
                <p className="text-3xl sm:text-4xl font-extrabold text-brand-accent mb-1">
                  {point.metric}
                </p>
                <p className="text-sm font-bold text-neutral-900 dark:text-white mb-1">{point.label}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Section 4: Problem Statement ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <Heading
            title={data.problem.headline}
            description={data.problem.description}
            showLine={false}
          />

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {data.problem.painPoints.map((point) => (
              <div
                key={point.title}
                className="rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-7 text-center hover:border-brand-accent/40 dark:hover:border-brand-accent/40 hover:-translate-y-1 hover:shadow-lg hover:shadow-neutral-200/50 transition-all duration-300"
              >
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white mb-2">{point.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Workflow Guide ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-5xl mx-auto relative z-10">
          <Heading
            title={data.workflowGuide.headline}
            description={data.workflowGuide.subtitle}
            showLine={false}
          />
          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-5 sm:left-6 top-6 bottom-6 w-px bg-brand-accent hidden sm:block" />

            <div className="space-y-6">
              {data.workflowGuide.steps.map((step, i) => (
                <div key={step.title} className="relative flex gap-5 sm:gap-7">
                  {/* Number circle */}
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-accent flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg shadow-neutral-300/30">
                    {i + 1}
                  </div>
                  {/* Card */}
                  <div className="flex-1 rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-7 hover:border-brand-accent/40 dark:hover:border-brand-accent/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-neutral-200/50 transition-all duration-300">
                    <h3 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white mb-2">{step.title}</h3>
                    <p className="text-sm sm:text-[15px] text-neutral-600 dark:text-neutral-400 leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Quick Steps ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <Heading
            title={data.steps.headline}
            showLine={false}
          />

          <div className="grid sm:grid-cols-3 gap-6">
            {data.steps.items.map((step, i) => (
              <div key={step.title} className="text-center rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 bg-white dark:bg-neutral-900/40 p-6 sm:p-8">
                <div className="w-12 h-12 rounded-full bg-brand-accent text-white font-bold text-lg flex items-center justify-center mx-auto mb-5 shadow-md shadow-neutral-300/20">
                  {i + 1}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Button
              size="lg"
              className="capitalize font-bold px-8 py-5 rounded-full bg-primary-gradient text-primary-foreground text-base shadow-md shadow-primary/20 hover:brightness-[0.97] hover:-translate-y-0.5 transition-all duration-300 group"
              onClick={() => handleCTA(`CTA_UseCase_Steps_${data.slug}`, "Start For Free")}
            >
              {t.useCaseLayout.startForFree}
              <HugeiconsIcon icon={ArrowRight02Icon} size={20} color="currentColor" className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ Section 4: Example Flows ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <Heading
            title={data.flows.headline}
            description={data.flows.subtitle}
            showLine={false}
          />

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
            {data.flows.items.map((flow) => (
              <div
                key={flow.title}
                className="group rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 hover:border-brand-accent/40 dark:hover:border-brand-accent/40 hover:-translate-y-1 hover:shadow-lg hover:shadow-neutral-200/50 transition-all duration-300"
              >
                {/* Header with icon + title */}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-11 h-11 rounded-xl ${data.color.bg} flex items-center justify-center shrink-0`}>
                    <UseCaseIcon name={flow.icon} size={20} className={data.color.text} />
                  </div>
                  <h3 className="text-base font-bold text-neutral-900 dark:text-white">{flow.title}</h3>
                </div>

                {/* Trigger → Response visual flow */}
                <div className="flex items-stretch gap-3">
                  {/* Trigger */}
                  <div className="flex-1 rounded-xl bg-neutral-100/80 dark:bg-neutral-800/50 p-3.5">
                    <p className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1.5">{t.useCaseLayout.trigger}</p>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-white leading-snug">{flow.trigger}</p>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center shrink-0">
                    <div className="w-8 h-8 rounded-full bg-brand-accent/10 dark:bg-brand-accent/15 flex items-center justify-center group-hover:bg-brand-accent/20 transition-colors">
                      <HugeiconsIcon icon={ArrowRight02Icon} size={14} color="currentColor" className="text-brand-accent group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>

                  {/* Response */}
                  <div className="flex-1 rounded-xl bg-brand-accent/5 dark:bg-brand-accent/10 border border-brand-accent/15 dark:border-brand-accent/20 p-3.5">
                    <p className="text-[10px] font-bold text-brand-accent dark:text-brand-accent-light uppercase tracking-wider mb-1.5">{t.useCaseLayout.response}</p>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-white leading-snug">{flow.response}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Testimonial ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-3xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-accent/10 dark:bg-brand-accent/15 mb-6">
            <HugeiconsIcon icon={QuoteUpIcon} size={20} color="currentColor" className="text-brand-accent" />
          </div>
          <blockquote className="text-lg sm:text-xl md:text-2xl font-medium text-neutral-800 dark:text-neutral-200 leading-relaxed mb-6">
            "{data.testimonial.quote}"
          </blockquote>
          <div>
            <p className="font-bold text-neutral-900 dark:text-white text-sm">{data.testimonial.name}</p>
            <p className="text-neutral-500 dark:text-neutral-400 text-sm">{data.testimonial.handle} · {data.testimonial.role}</p>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-3xl mx-auto relative z-10">
          <Heading
            title={t.useCaseLayout.frequentlyAsked}
            highlight={<span className="gradient-text">{t.useCaseLayout.questionsHighlight}</span>}
            showLine={false}
          />

          <Accordion type="single" collapsible className="w-full space-y-2 sm:space-y-3">
            {data.faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-4 sm:px-6 bg-white dark:bg-neutral-900/50 hover:border-neutral-300 dark:hover:border-neutral-700 transition-all duration-300 data-[state=open]:border-brand-button/50"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold py-3 sm:py-4 hover:no-underline text-neutral-900 dark:text-white [&[data-state=open]]:text-brand-button">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 dark:text-neutral-400 text-sm sm:text-base pb-3 sm:pb-4 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═══ Section 6: Related Use Cases ═══ */}
      {relatedUseCases.length > 0 && (
        <section className="w-full py-10 sm:py-20 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-neutral-100 dark:border-white/5 relative overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <Heading
              title={t.useCaseLayout.exploreMore}
              showLine={false}
            />
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
              {relatedUseCases.map((uc) => (
                <Link
                  key={uc.slug}
                  to={`/use-case/${uc.slug}` as string}
                  className="flex items-center justify-between rounded-xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 px-5 py-5 hover:border-brand-accent/40 dark:hover:border-brand-accent/40 hover:-translate-y-1 hover:shadow-lg hover:shadow-neutral-200/50 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${uc.color.bg} flex items-center justify-center shrink-0`}>
                      <UseCaseIcon name={uc.icon} size={16} className={uc.color.text} />
                    </div>
                    <span className="text-sm font-semibold text-neutral-900 dark:text-white">{uc.name}</span>
                  </div>
                  <HugeiconsIcon icon={ArrowRight02Icon} size={16} color="currentColor" className="text-neutral-400 group-hover:text-brand-accent group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ Section 7: Bottom CTA ═══ */}
      <CTA />

      {/* Floating AI Widget */}
      <AskAIExplain
        context="generic"
        meta={{ pageTitle: data.name, pageUrl: `https://zapify.pro/use-case/${data.slug}` }}
        isWidget
      />
    </div>
  )
}
